<template>
  <el-card class="box-card">
    <div
        slot="header"
        class="clearfix">
      <span class="header-item">{{ $t('details.title') }}</span>
      <span class="ring-card__header-buttons">
        <a
            v-if="!editMode"
            @click="editForm"> {{ $t('common.edit') }}</a>
        <a
            v-if="editMode"
            class="save-edit-mode"
            @click="submitForm"> {{ 'Save ' }}</a>
        <a
            v-if="editMode"
            @click="cancelForm"> {{ 'Cancel' }}</a>
      </span>
    </div>
    <div
        v-loading="updating.loading || customer.id === undefined "
        class="card-content">
      <el-form
          ref="profileForm"
          :model="updating.form"
          :rules="profileForm.rules"
          :inline="true">
        <el-row>
          <el-col :span="8">
            <div class="ring-card__input-label">
              {{ $t('details.full_name') }}
            </div>
            <div
                v-show="editMode && allowEditName"
                class="ring-card__input cus-details-fullname__edit">
              <el-form-item prop="firstName">
                <el-input
                    v-model="updating.form.firstName"
                    size="small"
                    placeholder="First name"
                    inline-message="true" />
              </el-form-item>
              <el-form-item prop="lastName">
                <el-input
                    v-model="updating.form.lastName"
                    size="small"
                    placeholder="Last name"
                    inline-message="true" />
              </el-form-item>
            </div>
            <div
                v-show="!editMode || !allowEditName"
                class="ring-card__input">
              {{ customer.first_name }} {{ customer.last_name }}
            </div>
          </el-col>
          <el-col :span="8">
            <div class="ring-card__input-label">
              {{ $t('details.primary_email') }}
              <span v-if="allowEditEmail">
                <customer-email-form
                    v-acl.enable="{permission: 'update-primary-email', on: 'Customer'}"
                    :customer="customer"
                    :update-fn="updateCustomerEmail" />
              </span>
            </div>
            <div
                class="ring-card__input"
                style="display: block;">
              {{ customer.email }}
            </div>
          </el-col>
          <el-col :span="8">
            <div class="ring-card__input-label">
              {{ $t('details.created_at') }}
            </div>
            <div class="ring-card__input">
              <time-zone-formatter :current-time="customer.created_at" />
            </div>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="8">
            <div class="ring-card__input-label">
              {{ $t('details.secondary_email') }}
            </div>
            <div
                v-show="editMode"
                class="ring-card__input cus-details-field__edit">
              <el-form-item prop="altEmail">
                <el-input
                    v-model="updating.form.altEmail"
                    v-acl.enable="{permission: 'update-secondary-email', on: 'Customer'}"
                    size="small"
                    placeholder="Secondary email"
                    inline-message="true" />
              </el-form-item>
            </div>
            <div
                v-show="!editMode"
                class="ring-card__input">
              {{ altEmail || "N/A" }}
            </div>
          </el-col>
          <el-col :span="8">
            <div class="ring-card__input-label">
              Legal Country
            </div>
            <div class="ring-card__input">
              {{ legalCountry }}
            </div>
          </el-col>
          <el-col :span="8">
            <div class="ring-card__input-label">
              {{ $t('details.phone') }}
            </div>
            <div
                v-if="editMode"
                class="ring-card__input cus-details-field__edit">
              <el-form-item prop="phoneNumber">
                <el-input
                    v-model="updating.form.phoneNumber"
                    size="small"
                    placeholder="Phone number"
                    inline-message="true" />
              </el-form-item>
            </div>
            <div
                v-else
                class="ring-card__input">
              {{ customer.phone_number || 'No number' }}
            </div>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="8">
            <div class="ring-card__input-label">
              {{ $t('details.referrals') }}
            </div>
            <div class="ring-card__input">
              N/A
            </div>
          </el-col>
          <el-col
              v-acl.show="{permission: 'fraud-report', on: 'Customer'}"
              :span="8">
            <div class="ring-card__input-label">
              Fraud Detection
            </div>
            <div class="ring-card__input">
              <el-popover
                  v-model="toggleFraudModal"
                  placement="top"
                  title="Fraud Detection"
                  width="250"
                  trigger="click">
                <p
                    v-if="!customer.profile.fraud"
                    style="word-break: break-word;font-size: 13px; color: #f56c6c; text-align: center;">
                  <i class="el-icon-warning"></i>
                  Are you sure to send a fraud report for this customer?
                </p>
                <p
                    v-else
                    style="word-break: break-word;font-size: 13px; color: #f56c6c; text-align: center;">
                  Are you sure to remove this fraud report for the customer?
                </p>
                <div style="text-align: right; margin: 0">
                  <el-button
                      size="mini"
                      type="text"
                      @click="toggleFraudModal = false">
                    cancel
                  </el-button>
                  <el-button
                      type="primary"
                      size="mini"
                      @click="reportFraud()">
                    confirm
                  </el-button>
                </div>
                <el-switch
                    slot="reference"
                    :value="customer.profile.fraud" />
              </el-popover>
            </div>
          </el-col>
          <el-col
              v-acl.show="{permission: 'view', on: 'DCCS3' }"
              :span="8">
            <div class="ring-card__input-label">
              {{ $t('details.account_type') }}
            </div>
            <div class="ring-card__input">
              {{ accountTypeDescription }}
            </div>
          </el-col>
        </el-row>
        <el-row>
          <el-col
              :span="8">
            <div class="ring-card__input-label">
              2SV Status
            </div>
            <div class="ring-card__input">
              <TSVStatus
                  :is-on="tfaEnabled"
                  :phone="tfaPhone" />
            </div>
          </el-col>
          <el-col
              v-acl.show="{permission: 'accountSummary', on: 'NewUI'}"
              :span="8">
            <div class="ring-card__input-label">
              Account Locked
            </div>
            <div class="ring-card__input">
              <LockAccountSwitch />
            </div>
          </el-col>
          <el-col
              v-acl.show="{permission: 'reset-password', on: 'Customer' }"
              :span="8">
            <el-popover
                v-model="confirmVisible"
                placement="top">
              <p>Are you sure to reset password?</p>
              <div style="text-align: right; margin: 0">
                <el-button
                    type="text"
                    size="mini"
                    @click="cancelReset()">
                  Cancel
                </el-button>
                <el-button
                    type="primary"
                    size="mini"
                    @click="sendResetPassword()">
                  Yes, confirm
                </el-button>
              </div>
              <a
                  v-show="allowResetPassword"
                  slot="reference"
                  :class="['ring-card__text--actionable', loadingReset ? 'disabled' : '']">
                <i
                    v-if="loadingReset"
                    class="el-icon-loading" />
                {{ $t('details.reset_password') }}
              </a>
            </el-popover>
          </el-col>
        </el-row>
      </el-form>
      <el-alert
          v-if="updating.errors"
          style="margin-top: 20px"
          title="Saving error"
          type="warning"
          description="Customer details could not be updated"
          show-icon />
    </div>
  </el-card>
</template>

<script>
import { isEmpty, set, get } from "lodash";
import { createNamespacedHelpers } from "vuex";
const { mapActions, mapGetters } = createNamespacedHelpers("db/account");
import TSVStatus from "@/components/Shared/Account/TSVStatus";
import CustomerEmailForm from "@/components/Shared/Account/CustomerEmailForm";
import LockAccountSwitch from "@/components/Shared/Account/LockAccountSwitch";

export default {
  name: "Details",
  components: {
    TSVStatus,
    CustomerEmailForm,
    LockAccountSwitch,
  },
  props: {
    customer: {
      type: Object,
      default: () => {
        return {
          client_devices: [],
        };
      },
    },
  },
  data() {
    return {
      confirmVisible: false,
      loadingReset: false,
      editMode: false,
      toggleFraudModal: false,
      updating: {
        loading: false,
        errors: null,
        form: {
          firstName: "",
          lastName: "",
          altEmail: "",
          phoneNumber: null,
        },
      },
      profileForm: {
        rules: {
          firstName: [
            {
              required: true,
              pattern: /[\s\w]{1,20}$/,
              message: "Please input First name",
              trigger: "blur",
            },
          ],
          lastName: [
            {
              required: true,
              pattern: /[\s\w]{1,30}$/,
              message: "Please input Last name",
              trigger: "blur",
            },
          ],
          email: [
            {
              type: "email",
              required: true,
              message: "Please input a valid email",
              trigger: "blur",
            },
          ],
          altEmail: [
            {
              type: "email",
              required: false,
              message: "Please input a valid email",
              trigger: "blur",
            },
          ],
          phoneNumber: [
            {
              pattern: /^\d{7,15}$/,
              required: false,
              message: "Please input a phone number (between 7 and 15 digits)",
              trigger: "blur",
            },
          ],
        },
      },
    };
  },
  computed: {
    ...mapGetters(["legalCountry", "accountVerified"]),
    hasClientDevices() {
      return !isEmpty(this.customer.client_devices);
    },
    altEmail() {
      return get(this.customer, "profile.alt_email", "");
    },
    tfaEnabled() {
      return Boolean(this.customer["tfa_enabled"]);
    },
    tfaPhone() {
      return this.customer["tfa_phone_number"];
    },
    isRingAccount() {
      return get(this.customer, "account_type", null) === "ring";
    },
    accountTypeDescription() {
      if (get(this.customer, "account_type", null) === "amazon") return "LWA";
      else if (get(this.customer, "amazon_profile", null))
        return "Amazon-linked";
      else return "Ring";
    },
    allowEditName() {
      return this.isRingAccount;
    },
    allowEditEmail() {
      let canEdit = get(this.customer, "allowEditEmail", false);
      return this.isRingAccount && canEdit;
    },
    allowResetPassword() {
      return this.isRingAccount;
    },
  },
  methods: {
    get: get,
    ...mapActions(["resetPassword", "updateCustomer", "updateCustomerEmail"]),
    cancelReset() {
      this.confirmVisible = false;
    },
    sendResetPassword() {
      this.confirmVisible = false;
      this.loadingReset = true;
      this.resetPassword()
        .then((response) => {
          this.$message({
            showClose: true,
            message: "Reset password email sent to the customer.",
            type: "success",
          });
        })
        .catch((error) => {
          console.log("Error", error);
        })
        .finally(() => {
          this.loadingReset = false;
        });
    },
    buildPayload() {
      const form = this.updating.form;
      const cus = this.customer;
      let data = {};
      if (form.firstName !== cus.first_name)
        data["first_name"] = form.firstName;
      if (form.lastName !== cus.last_name) data["last_name"] = form.lastName;
      if (form.email !== cus.email) data["email"] = form.email;
      if (form.phoneNumber !== cus.phone_number)
        data["phone_number"] = form.phoneNumber;
      if (form.altEmail !== get(this.customer, "profile.alt_email", ""))
        set(data, "profile.alt_email", form.altEmail);

      return { data, meta: { email: this.customer.email } };
    },
    saveForm() {
      const payload = this.buildPayload();
      if (isEmpty(payload)) return; // TODO: not field was edited

      this.updating.loading = true;
      this.updateCustomer({
        id: this.customer.id,
        payload,
      })
        .then((_) => {})
        .catch((error) => {
          this.updating.errors = error;
        })
        .finally(() => {
          this.updating.loading = false;
          this.cancelForm();
        });
    },
    reportFraud() {
      let payload = {
        data: {
          profile: { fraud: !this.customer.profile.fraud },
        },
        meta: {
          email: this.customer.email,
        },
      };
      this.updating.loading = true;
      this.toggleFraudModal = false;
      this.updateCustomer({
        id: this.customer.id,
        payload,
      })
        .then()
        .catch((error) => {
          console.log("error", error);
        })
        .finally(() => {
          this.updating.loading = false;
        });
    },
    cancelForm() {
      this.editMode = false;
      this.resetForm();
    },
    editForm() {
      this.editMode = true;
      this.updating.form.firstName = this.customer.first_name;
      this.updating.form.lastName = this.customer.last_name;
      this.updating.form.email = this.customer.email;
      this.updating.form.phoneNumber = this.customer.phone_number;
      this.updating.form.altEmail = get(this.customer, "profile.alt_email", "");
    },
    submitForm() {
      this.$refs["profileForm"].validate((valid) => {
        if (valid) {
          this.saveForm();
        } else {
          return false;
        }
      });
    },
    resetForm() {
      this.$refs["profileForm"].resetFields();
    },
  },
};
</script>

<style lang="scss">
a.disabled {
  pointer-events: none;
  color: #999;
  opacity: 0.5;
}
.view-link {
  color: #599ce7;
  text-decoration: none;
}
.col-padd0 {
  padding: 0px !important;
}
.text-red {
  color: $--color-milano-red;
}

.box-card .ring-card__header-buttons a.save-edit-mode {
  color: $--color-milano-red;
}
.pull-right {
  float: right;
}
.cus-details-fullname__edit {
  .el-form-item {
    margin-bottom: 0px;
    .el-form-item__content {
      width: fit-content;
    }
  }
}

.cus-details-field__edit {
  .el-form-item {
    margin-bottom: 0px;
    width: 98%;
    .el-form-item__content {
      width: inherit;
    }
  }
}
</style>
